import { create } from 'zustand';
import { authService, User } from '../services/authService';

interface RegisterData {
  username: string;
  email: string;
  password: string;
  phoneCountryCode?: number;
  phoneNumber?: string;
  organization?: string;
  callSign?: string;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  clearError: () => void;
  initializeAuth: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,

  login: async (username: string, password: string) => {
    set({ isLoading: true, error: null });
    
    try {
      const response = await authService.login({ username, password });
      set({
        user: response.user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
      
      // 触发登录成功事件
      window.dispatchEvent(new CustomEvent('auth-success'));
    } catch (error) {
      set({
        isLoading: false,
        error: error instanceof Error ? error.message : 'Login failed',
      });
      throw error;
    }
  },

  logout: async () => {
    set({ isLoading: true });
    
    try {
      // 调用 authService.logout() 会处理MQTT清理和触发登出事件
      await authService.logout();
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
      
      // 注意：authService.logout() 已经触发了 'auth-logout' 事件
      // 这里不需要重复触发
    } catch (error) {
      console.error('Logout error:', error);
      set({ isLoading: false });
    }
  },

  register: async (data: RegisterData) => {
    set({ isLoading: true, error: null });
    
    try {
      await authService.register(data);
      set({ isLoading: false, error: null });
    } catch (error) {
      set({
        isLoading: false,
        error: error instanceof Error ? error.message : 'Registration failed',
      });
      throw error;
    }
  },

  clearError: () => {
    set({ error: null });
  },

  initializeAuth: () => {
    try {
      const user = authService.getUser();
      const isAuthenticated = authService.isAuthenticated();
      
      set({
        user,
        isAuthenticated,
      });
    } catch (error) {
      console.error('初始化认证状态失败:', error);
      // 如果初始化失败，设置为未认证状态
      set({
        user: null,
        isAuthenticated: false,
      });
    }
  },
})); 